/** @typedef {import("../../../api/types").Order} Order */
import React, {useState} from "react";
import axios from "axios";
import {Link} from "react-router-dom";
import {copyPromisedText} from "../../../shared/utils/clipboard";
import {orderPublicLink} from "../utils/orderLink";

const ORDERS_URL = "/os/api/service_orders/";

// Why a Pix code couldn't be made, by the API's `code`.
const PIX_MESSAGES = {
    pix_currency_not_supported: "O Pix só funciona com ordens em reais (R$).",
    pix_not_available: "O status da ordem mudou e não é mais possível gerar o link Pix.",
};

/**
 * What the provider copies to send to the customer: the link to the order and
 * the Pix Copia e Cola (or its QR code). Each copy leaves a `notice` to show (and, when the
 * browser would not let us copy the Pix, the code itself to copy by hand).
 * The menu that starts them is ShareMenu; the notice is ShareNotice.
 *
 * @param {Order} order
 * @param {(order: Order) => void} onUpdated
 */
export const useShare = (order, onUpdated) => {
    const [pixBusy, setPixBusy] = useState(false);
    const [notice, setNotice] = useState(/** @type {{color: string, content: React.ReactNode}|null} */ (null));
    const [pixCode, setPixCode] = useState(/** @type {string|null} */ (null));
    const [qrCode, setQrCode] = useState(/** @type {string|null} */ (null));
    const url = `${ORDERS_URL}${order.id}/`;

    const copyLink = async () => {
        const link = orderPublicLink(order);
        if (!link) return;
        try {
            await copyPromisedText(Promise.resolve(link));
            setNotice({color: "success", content: "Link da ordem copiado para a área de transferência."});
        } catch {
            // The browser would not let us copy it: show it to be copied by hand.
            setNotice({color: "warning", content: <>Copie o link da ordem: {link}</>});
        }
    };

    // Why a Pix code couldn't be made, as a notice.
    const explainPix = async (err) => {
        const code = err.response?.data?.code;
        if (code === "pix_key_missing") {
            setNotice({
                color: "warning",
                content: (
                    <>
                        Você ainda não cadastrou sua chave Pix.{" "}
                        <Link to="/config/edit">Cadastrar em Configurações</Link>
                    </>
                ),
            });
        } else if (PIX_MESSAGES[code]) {
            setNotice({color: "danger", content: PIX_MESSAGES[code]});
            if (code === "pix_not_available") {
                try {
                    onUpdated((await axios.get(url)).data);
                } catch {
                    // Keep the stale view; the message above still applies.
                }
            }
        } else {
            setNotice({color: "danger", content: "Não foi possível gerar o link Pix. Tente novamente."});
        }
    };

    const copyPix = async () => {
        setPixBusy(true);
        setNotice(null);
        const payload = axios.get(`${url}pix/`).then(({data}) => /** @type {string} */ (data.payload));
        payload.catch(() => {}); // handled below; avoids an "unhandled" warning while copying
        try {
            await copyPromisedText(payload);
            setNotice({color: "success", content: "Link Pix copiado para a área de transferência."});
        } catch {
            try {
                // The code is fine; the browser just wouldn't let us copy it.
                setPixCode(await payload);
            } catch (err) {
                await explainPix(err);
            }
        } finally {
            setPixBusy(false);
        }
    };

    // The same code, as a QR code the customer reads from this screen.
    const showQr = async () => {
        setPixBusy(true);
        setNotice(null);
        try {
            const {data} = await axios.get(`${url}pix/`);
            setQrCode(data.payload);
        } catch (err) {
            await explainPix(err);
        } finally {
            setPixBusy(false);
        }
    };

    return {
        notice,
        dismissNotice: () => setNotice(null),
        pixCode,
        closePixCode: () => setPixCode(null),
        qrCode,
        closeQrCode: () => setQrCode(null),
        pixBusy,
        copyLink,
        copyPix,
        showQr,
    };
};
